import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAraAuth } from '../contexts/AraAuthContext';
import { useAraTheme } from '../contexts/AraThemeContext';
import { getAllAraEncrypted, deleteAraData, ARA_STORES } from '../utils/ara-storage';
import { ArrowLeft, NotePencil, Trash, X } from 'phosphor-react';
import { toast } from 'sonner';

const stripMarkdown = (t) => (t || '')
  .replace(/\*\*(.+?)\*\*/g, '$1').replace(/__(.+?)__/g, '$1')
  .replace(/(^|[\s(])\*([^*\n]+?)\*(?=[\s.,!?)]|$)/g, '$1$2')
  .replace(/(^|[\s(])_([^_\n]+?)_(?=[\s.,!?)]|$)/g, '$1$2')
  .replace(/`([^`\n]+)`/g, '$1')
  .replace(/^#{1,6}\s+/gm, '').replace(/^[\s]*[-*+]\s+/gm, '').replace(/^>\s+/gm, '');

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const formatTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

const AraReflections = () => {
  const [reflections, setReflections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const { encryptionKey } = useAraAuth();
  const { theme } = useAraTheme();
  const navigate = useNavigate();
  const logo = theme === 'dark' ? '/ara-logo-dark.png' : '/ara-logo-light.png';

  const load = async () => {
    if (!encryptionKey) return;
    setIsLoading(true);
    try {
      const data = await getAllAraEncrypted(ARA_STORES.REFLECTIONS, encryptionKey).catch(() => []);
      data.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setReflections(data);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, [encryptionKey]);

  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => { if (e.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  const remove = async (id) => {
    if (!window.confirm('Delete this reflection? This cannot be undone.')) return;
    try {
      await deleteAraData(ARA_STORES.REFLECTIONS, id);
      setReflections((prev) => prev.filter((r) => r.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success('Reflection deleted');
    } catch {
      toast.error('Could not delete');
    }
  };

  return (
    <div className="ara-root min-h-screen" data-ara-theme={theme} data-testid="ara-reflections">
      <header className="ara-header sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/ara/chat')}
            className="inline-flex items-center gap-2 ara-icon-btn p-2 rounded-lg text-sm"
            data-testid="ara-back-to-chat-btn"
            aria-label="Back to Ara"
          >
            <ArrowLeft size={18} /> <span className="hidden sm:inline">Back to Ara</span>
          </button>
          <img src={logo} alt="" className="w-8 h-8 rounded-full ml-1" />
          <h1 className="font-serif font-semibold ara-text" data-testid="ara-reflections-title">My Reflections</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {isLoading ? (
          <div className="text-center py-16 ara-text-muted">Opening your reflections…</div>
        ) : reflections.length === 0 ? (
          <div className="text-center py-16" data-testid="ara-reflections-empty">
            <NotePencil size={32} weight="duotone" className="ara-accent mx-auto mb-3" />
            <p className="ara-text font-medium mb-1">No reflections yet</p>
            <p className="text-sm ara-text-muted max-w-sm mx-auto">
              When a conversation feels worth remembering, tap the bookmark in Ara
              to save it here. Only reflections you choose to save are kept.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {reflections.map((r) => (
              <div
                key={r.id}
                className="ara-card rounded-2xl p-5 cursor-pointer hover:ara-card-hover transition-all"
                onClick={() => setSelected(r)}
                data-testid={`ara-reflection-${r.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium ara-text mb-1 truncate">{r.title || 'Untitled reflection'}</h3>
                    <p className="text-xs ara-text-muted">
                      {formatDate(r.createdAt)} • {formatTime(r.createdAt)}
                      {typeof r.messageCount === 'number' && (
                        <span> • {r.messageCount} messages</span>
                      )}
                    </p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); remove(r.id); }}
                    className="ara-icon-btn p-2 rounded-lg"
                    data-testid={`ara-reflection-delete-${r.id}`}
                    aria-label="Delete reflection"
                  >
                    <Trash size={16} weight="duotone" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Inline detail panel */}
      {selected && (
        <div
          className="fixed inset-0 z-20 ara-root flex flex-col"
          data-ara-theme={theme}
          data-testid="ara-reflection-detail"
          role="dialog"
          aria-modal="true"
          aria-label="Reflection detail"
        >
          <header className="ara-header sticky top-0 z-10">
            <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
              <button
                onClick={() => setSelected(null)}
                className="inline-flex items-center gap-2 ara-icon-btn p-2 rounded-lg text-sm"
                data-testid="ara-reflection-back-btn"
              >
                <ArrowLeft size={18} /> <span>Back to Reflections</span>
              </button>
              <button
                onClick={() => setSelected(null)}
                className="ara-icon-btn p-2 rounded-lg sm:hidden"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </header>
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-3xl mx-auto px-4 py-6 pb-12">
              <h2 className="text-2xl font-serif font-semibold ara-text mb-2" data-testid="ara-reflection-detail-title">
                {selected.title || 'Untitled reflection'}
              </h2>
              <p className="text-xs ara-text-muted mb-6">
                {formatDate(selected.createdAt)} • {formatTime(selected.createdAt)}
              </p>
              {Array.isArray(selected.messages) && selected.messages.length > 0 ? (
                <div className="space-y-4" data-testid="ara-reflection-detail-messages">
                  {selected.messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] rounded-2xl px-5 py-3 ${m.role === 'user' ? 'ara-msg-user' : 'ara-msg-assistant'}`}>
                        <div className="text-[11px] uppercase tracking-wide ara-text-muted mb-1 font-medium">
                          {m.role === 'user' ? 'You' : 'Ara'}
                        </div>
                        <p className="whitespace-pre-wrap leading-relaxed">
                          {m.role === 'assistant' ? stripMarkdown(m.content) : m.content}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="ara-card rounded-2xl p-6 text-center" data-testid="ara-reflection-detail-empty">
                  <p className="ara-text font-medium mb-1">This reflection's messages aren't available to display.</p>
                  <p className="text-sm ara-text-muted">Your entry remains saved and untouched. You can return and continue reflecting with Ara anytime.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AraReflections;
